/**
 * Parse stored user.dob (DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD) for edit forms.
 */
export function parseUserDob(dob) {
  const s = String(dob || '').trim();
  if (!s) return { day: '', month: '', year: '' };

  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (slash) {
    return { day: slash[1], month: slash[2], year: slash[3] };
  }

  const dash = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s);
  if (dash) {
    return { day: dash[1], month: dash[2], year: dash[3] };
  }

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) {
    return { day: iso[3], month: iso[2], year: iso[1] };
  }

  return { day: '', month: '', year: '' };
}

/** Compute age in years from day/month/year form parts (returns null if invalid). */
export function computeAgeFromDobParts(day, month, year) {
  const d = parseInt(String(day || '').trim(), 10);
  const m = parseInt(String(month || '').trim(), 10);
  const y = parseInt(String(year || '').trim(), 10);
  if (
    !Number.isFinite(d) ||
    !Number.isFinite(m) ||
    !Number.isFinite(y) ||
    d < 1 ||
    d > 31 ||
    m < 1 ||
    m > 12 ||
    y < 1900 ||
    y > 2100
  ) {
    return null;
  }
  const birth = new Date(y, m - 1, d);
  if (birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

/** Build YYYY-MM-DD for input type="date" from day/month/year parts. */
export function dobPartsToIsoDate(day, month, year) {
  const d = parseInt(String(day || '').trim(), 10);
  const m = parseInt(String(month || '').trim(), 10);
  const y = parseInt(String(year || '').trim(), 10);
  if (computeAgeFromDobParts(day, month, year) == null) return '';
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Split YYYY-MM-DD from input type="date" into day/month/year parts. */
export function isoDateToDobParts(iso) {
  const s = String(iso || '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return { day: '', month: '', year: '' };
  return {
    year: match[1],
    month: String(parseInt(match[2], 10)),
    day: String(parseInt(match[3], 10)),
  };
}

/** Compute age from YYYY-MM-DD (input type="date"). */
export function computeAgeFromIsoDate(iso) {
  const { day, month, year } = isoDateToDobParts(iso);
  return computeAgeFromDobParts(day, month, year);
}

/**
 * Prefer age from user.dob; fall back to a finite stored age.
 * Never treats Number(null) as 0.
 */
export function resolveAgeFromUser(user) {
  if (!user || typeof user !== 'object') return null;
  const parts = parseUserDob(user.dob);
  const fromDob = computeAgeFromDobParts(parts.day, parts.month, parts.year);
  if (fromDob != null) return fromDob;
  if (user.age == null || user.age === '') return null;
  const n = Number(user.age);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
